import { toast } from '@teamsparta/stack-toast';
import type { NodeViewProps } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import { postCommunityPostUploadImage } from '@/api/community/post';

const MIN_WIDTH = 100;

type ResizeSide = 'left' | 'right' | null;

export function useResizeImage({
  editor,
  getPos,
  node,
  imageRef,
}: NodeViewProps & {
  imageRef: React.RefObject<HTMLImageElement>;
}) {
  const [resizeSide, setResizeSide] = useState<ResizeSide>(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const startResize = useCallback(
    (
      event:
        | React.MouseEvent<HTMLDivElement>
        | React.TouchEvent<HTMLDivElement>,
      side: ResizeSide,
    ) => {
      event.preventDefault();
      setResizeSide(side);

      if ('touches' in event) {
        setStartX(event.touches[0].clientX);
      } else {
        setStartX(event.clientX);
      }

      if (imageRef.current) {
        setStartWidth(imageRef.current.offsetWidth);
      }
    },
    [imageRef],
  );

  const dispatchNodeView = useCallback(
    (newAttrs?: Record<string, string>) => {
      const { tr: transaction } = editor.view.state;
      transaction.setNodeMarkup(getPos(), null, {
        ...node.attrs,
        style: `${imageRef.current?.style.cssText}`,
        width: imageRef.current?.width,
        ...newAttrs,
      });

      editor.view.dispatch(transaction);
    },
    [editor.view, getPos, imageRef, node.attrs],
  );

  useEffect(() => {
    if (!resizeSide) {
      return;
    }

    function handleMouseMove(event: MouseEvent | TouchEvent) {
      if (!resizeSide || !imageRef.current) {
        return;
      }

      let deltaX = 0;
      if ('touches' in event) {
        deltaX = event.touches[0].clientX - startX;
      } else {
        deltaX = event.clientX - startX;
      }
      const newWidth =
        resizeSide === 'right'
          ? Math.max(startWidth + deltaX, MIN_WIDTH)
          : Math.max(startWidth - deltaX, MIN_WIDTH);

      imageRef.current.style.width = newWidth + 'px';
      imageRef.current.setAttribute('width', newWidth.toString());
    }

    function handleMouseUp() {
      setResizeSide(null);

      if (!imageRef.current) {
        return;
      }

      dispatchNodeView();
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [dispatchNodeView, imageRef, resizeSide, startWidth, startX]);

  return { startResize, imageRef };
}

type ImagePosition = 'start' | 'end' | 'center';

export function useAlignImage({
  editor,
  getPos,
  node,
  imageRef,
}: NodeViewProps & {
  imageRef: React.RefObject<HTMLImageElement>;
}) {
  const [imagePosition, setImagePosition] = useState<ImagePosition>(
    node?.attrs?.position ?? 'center',
  );

  const dispatchNodeView = useCallback(
    (newAttrs?: Record<string, string>) => {
      const { tr: transaction } = editor.view.state;
      transaction.setNodeMarkup(getPos(), null, {
        ...node.attrs,
        style: `${imageRef.current?.style.cssText}`,
        width: imageRef.current?.offsetWidth,
        ...newAttrs,
      });

      editor.view.dispatch(transaction);
    },
    [editor.view, getPos, imageRef, node.attrs],
  );

  const align = useCallback(
    (position: ImagePosition) => {
      setImagePosition(position);

      dispatchNodeView({ position });
    },
    [dispatchNodeView],
  );

  return { imagePosition, align };
}

export function useRemoveImage({
  editor,
  getPos,
  node,
}: Pick<NodeViewProps, 'editor' | 'getPos' | 'node'>) {
  const remove = useCallback(() => {
    const { tr: transaction } = editor.view.state;
    transaction.delete(getPos(), getPos() + node.nodeSize);

    editor.view.dispatch(transaction);
  }, [editor.view, getPos, node.nodeSize]);

  return { remove };
}

export function useClickOutsideImage({
  targetRef,
  onClickOutside,
}: {
  targetRef: React.RefObject<HTMLElement>;
  onClickOutside: () => void;
}) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const imageToolBar = document.getElementById('image-toolbar');

      if (
        targetRef.current &&
        !targetRef.current.contains(event.target as Node) &&
        !imageToolBar?.contains(event.target as Node)
      ) {
        onClickOutside();
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [targetRef, onClickOutside]);
}

export function insertImage({
  file,
  view,
  type,
}: {
  file: File;
  /**
   * EditorView, NodeType이 @tiptap/react에서 제공되지 않아 any로 처리
   */
  view: any;
  type: any;
}) {
  const fileReader = new FileReader();
  fileReader.readAsDataURL(file);
  fileReader.onload = () => {
    const image = new Image();
    image.src = fileReader.result as string;
    image.onload = async () => {
      const skeletonNode = type.create({ src: '', loading: 'true' });

      const position = view.state.selection.from;
      const transaction = view.state.tr.insert(position, skeletonNode);
      view.dispatch(transaction);

      try {
        const { imageUrl } = await postCommunityPostUploadImage({ file });

        if (imageUrl) {
          const imageNode = type.create({
            src: imageUrl,
            loading: 'false',
          });

          const transaction = view.state.tr.replaceWith(
            position,
            position + 1,
            imageNode,
          );
          view.dispatch(transaction);
        }
      } catch (error) {
        console.error(error);
        toast.error('이미지 삽입에 실패했습니다.');

        view.dispatch(view.state.tr.delete(position, position + 1));
      }
    };
  };
}
